'use client'

/**
 * SetFlowChart — l'andamento di un set come grafico.
 *
 * Cosa disegna, e perché proprio questo. Del set sappiamo una cosa sola: a ogni
 * **cambio di servizio** com'era il punteggio. Da lì si ricava lo **scarto**
 * (casa − ospiti) in quei momenti: è la curva che fa vedere dove la partita è
 * girata, le fughe e le rimonte. Ogni cambio è un pallino, e il colore del
 * pallino dice **chi è andato a battere** (in pallavolo, con il rally point, chi
 * riprende il servizio ha appena vinto lo scambio).
 *
 * Quello che il grafico non fa: dire chi ha fatto il punto. Il referto non lo
 * dice, e il merito resta della squadra. Per questo non c'è nessun numero
 * «punti di Tizio»: solo punteggi, scarti e chi era al servizio.
 *
 * Ogni pallino ha un'area di puntamento più grande di sé e un **callout** che
 * dice, per quel cambio, chi era al servizio. È un componente client per questo
 * (stato di hover/click), ma il disegno resta un SVG: i colori sono i token
 * della lega e la geometria è verificabile.
 */
import { useState } from 'react'
import { splitLineupName } from '@/lib/lineup-names'
import type { SetFlow } from '@/lib/match-analysis'

const W = 800
const H = 190
const PAD = { top: 44, right: 74, bottom: 44, left: 46 }
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom

/**
 * «n.16 DEL CORE DANIELE» → «DEL CORE D.».
 *
 * Il cognome può essere fatto di più parole e cominciare con una particella
 * (`DEL CORE`, `DI BRINDISI`, `DELLA`): si spezza con la **stessa funzione della
 * distinta**, che le particelle le conosce. Prendere la prima parola come
 * cognome produceva «DEL C. D.», cioè il nome di un altro.
 */
function serverLabel(player: string | null, fallback: number | null): string {
  if (!player) return fallback != null ? `n.${fallback}` : 'battitore n.d.'
  const clean = player.replace(/^n\.\d+\s*/, '').trim()
  const { firstName, lastName } = splitLineupName(clean)
  if (!lastName) return clean
  const iniziali = firstName
    .split(/\s+/)
    .filter(Boolean)
    .map((nome) => `${nome[0]}.`)
    .join(' ')
  return iniziali ? `${lastName} ${iniziali}` : lastName
}

/**
 * I valori che i due disegni — orizzontale e verticale — ricavano dal set.
 *
 * Stanno in **un posto solo** perché una sola deve essere la regola che colloca
 * un cambio sull'asse del tempo: la posizione è il numero di **scambi giocati**
 * (`home + away`), non l'indice del cambio. Fra un cambio di servizio e il
 * successivo si giocano più punti (la serie di chi batte, più lo scambio che
 * toglie il servizio): scalarli sull'indice li distanzia in modo finto e fa
 * cadere un punteggio come `19-17` in un punto che non è il suo. Duplicare questa
 * derivazione nei due grafici vorrebbe dire vederli divergere.
 */
function flowGeometry(flow: SetFlow) {
  const points = flow.turns.map((turn) => ({
    diff: turn.home - turn.away,
    home: turn.home,
    away: turn.away,
    side: turn.side,
    number: turn.number,
    player: turn.player,
  }))
  if (points.length === 0) return null

  const maxAbs = Math.max(2, ...points.map((p) => Math.abs(p.diff)))
  const totali = points.map((p) => p.home + p.away)
  const maxX = Math.max(1, totali[totali.length - 1] ?? 1)

  const final = points[points.length - 1]
  const peak = points.reduce((best, p) => (Math.abs(p.diff) > Math.abs(best.diff) ? p : best), points[0])
  const swings = points.reduce(
    (count, p, i) => (i > 0 && Math.sign(p.diff) !== Math.sign(points[i - 1].diff) && p.diff !== 0 ? count + 1 : count),
    0,
  )

  // Nel tie-break le squadre cambiano campo quando una arriva all'**ottavo
  // punto**: il referto lo registra («AL CAMBIO», e le riprese ricominciano in
  // un blocco nuovo) e qui si segna la stessa cosa sulla curva, nel turno in cui
  // succede. Negli altri set il campo si cambia a fine set, non a metà.
  const courtSwitch = flow.set === 5 ? points.findIndex((p) => Math.max(p.home, p.away) >= 8) : -1

  // **Tutti gli scambi**, non solo quelli in cui cambia il servizio. Fra una
  // ripresa e la successiva chi batte prende tutti gli scambi tranne l'ultimo
  // (regola del rally point), quindi anche i punti che il referto non registra sono
  // **determinati**, non da inventare: il grafico li disegna tutti, e il pallino
  // resta solo dove il referto registra il cambio.
  const timeline: Array<{ rally: number; diff: number; cambio: boolean }> = [{ rally: 0, diff: 0, cambio: false }]
  {
    let casa = 0
    let ospiti = 0
    flow.turns.forEach((turn) => {
      const altro = turn.side === 'home' ? 'away' : 'home'
      // Quanti scambi vince ciascuna delle due **in questo turno**: si legge
      // dalla differenza fra lo stato alla fine del turno e quello precedente,
      // non da una regola fissa, così l'ultimo turno (forzato sul parziale) non
      // fa eccezione.
      const puntiServitore = (turn.side === 'home' ? turn.home : turn.away) - (turn.side === 'home' ? casa : ospiti)
      const puntiRicevente = (altro === 'home' ? turn.home : turn.away) - (altro === 'home' ? casa : ospiti)
      // Prima chi batte (gli scambi che tiene), **poi** chi riceve: l'ultimo
      // scambio del turno è sempre di chi riceve, ed è quello che gli dà il
      // servizio. Nella classica 3-1 il turno del battitore sono i primi due
      // punti e il terzo è dell'avversario.
      const spingi = (chi: 'home' | 'away', cambio: boolean) => {
        if (chi === 'home') casa += 1
        else ospiti += 1
        timeline.push({ rally: casa + ospiti, diff: casa - ospiti, cambio })
      }
      for (let k = 0; k < puntiServitore; k++) spingi(turn.side, puntiRicevente === 0 && k === puntiServitore - 1)
      for (let k = 0; k < puntiRicevente; k++) spingi(altro, k === puntiRicevente - 1)
      // Un turno senza scambi (l'ultima riga viene forzata sul parziale ma il set
      // è già chiuso) non aggiunge barre: la barra si disegna per **scambio**,
      // non per turno. Il `cambio` resta comunque segnato dal pallino.
    })
  }

  return {
    points,
    timeline,
    maxAbs,
    totali,
    maxX,
    final,
    peak,
    swings,
    leaderSide: (peak.diff > 0 ? 'home' : 'away') as 'home' | 'away',
    courtSwitch,
  }
}

/** Palette del grafico dei set: quella del grafico della classifica
 *  (`src/components/portal/TeamTrendChart.tsx`). */
const SERIE_HOME = '#2f6ac4'
const SERIE_AWAY = '#e07840'

/**
 * La legenda del grafico: chi batte (il colore dei pallini), i numeri del set e
 * cosa si può toccare. La usano **entrambi** i disegni, così la stessa partita
 * non si legge in due modi a seconda dello schermo. `areaHint` aggiunge la riga
 * che serve solo in verticale, dove gli scarti vanno a sinistra e a destra
 * invece che sopra e sotto lo zero.
 */
function FlowCaption({
  flow,
  home,
  away,
  markers,
  homeColor = SERIE_HOME,
  awayColor = SERIE_AWAY,
  markerHint = true,
}: {
  flow: SetFlow
  home: string
  away: string
  markers: NonNullable<SetFlowChartProps['markers']>
  homeColor?: string
  awayColor?: string
  /** Elenca i segni nella legenda? Su mobile no: stanno sotto il grafico. */
  markerHint?: boolean
}) {
  const shortHome = home.trim().split(/\s+/)[0] ?? home
  const shortAway = away.trim().split(/\s+/)[0] ?? away

  return (
    <figcaption className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[color:var(--league-muted)]">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-2 w-2 rounded-full" style={{ background: homeColor }} />
        va a battere {shortHome}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-2 w-2 rounded-full" style={{ background: awayColor }} />
        va a battere {shortAway}
      </span>
      {markerHint && markers.length > 0 && <span>· segni: {markers.map((m) => m.label).join(', ')}</span>}
      {!flow.verified && <span>· lettura parziale del referto</span>}
    </figcaption>
  )
}

export interface SetFlowChartProps {
  flow: SetFlow
  home: string
  away: string
  /** Prefisso unico per gli id SVG (clip path) nella pagina. */
  idPrefix: string
  /**
   * Colori delle due squadre: gli **stessi del grafico della classifica**
   * (`TeamTrendChart`: accento del girone per la squadra di casa, arancio di lega
   * per la trasferta). Nel grafico dei set non si usano verde e rosso: lì
   * significano vinto/perso, e sulle due squadre direbbero la cosa sbagliata —
   * un rosso che vuol dire «la trasferta è avanti» si legge «la trasferta ha
   * perso».
   */
  homeColor?: string
  awayColor?: string
  /**
   * Le sostituzioni da segnare sul grafico. `index` è l'indice del turno in cui
   * il grafico le colloca: è la posizione che il referto assegna alla
   * sostituzione (non un istante, che il referto non registra).
   *
   * `labelSide` decide da che parte sta l'etichetta: i time-out sopra il
   * grafico, le sostituzioni sotto. Due file distinte, così i segni non si
   * accavallano e si capisce subito di che tipo è ciascuno.
   */
  markers?: Array<{
    /** Indice del turno: ignorato se c'è `total`. */
    index?: number
    /** Scambi giocati al momento del segno: `own + opp`, ed è esatto. */
    total?: number
    side: 'home' | 'away'
    label: string
    title?: string
    labelSide?: 'top' | 'bottom'
  }>
}

export function SetFlowChart({
  flow,
  home,
  away,
  idPrefix,
  markers = [],
  homeColor = SERIE_HOME,
  awayColor = SERIE_AWAY,
}: SetFlowChartProps) {
  const [attivo, setAttivo] = useState<number | null>(null)
  const geo = flowGeometry(flow)
  if (!geo) return null
  const { points, maxAbs, totali, maxX, final, courtSwitch } = geo

  // Scala simmetrica rispetto allo zero: lo zero sta in mezzo, così si vede a
  // colpo d'occhio chi era avanti e di quanto.
  const zeroY = PAD.top + PLOT_H / 2
  const xAt = (scambi: number) => PAD.left + Math.min(1, Math.max(0, scambi / maxX)) * PLOT_W
  const x = (index: number) => xAt(totali[index] ?? 0)
  const y = (diff: number) => zeroY - (diff / maxAbs) * (PLOT_H / 2)

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.diff).toFixed(1)}`).join(' ')
  const area = `${line} L${x(points.length - 1).toFixed(1)},${zeroY} L${x(0).toFixed(1)},${zeroY} Z`

  // Callout: ancorato in alto e richiuso dentro il disegno, con una linea guida
  // fino al pallino. In alto e non sopra il pallino perché i pallini sono
  // ravvicinati: un riquadro che salta di posizione è più difficile da leggere.
  const CALLOUT_W = 232
  const CALLOUT_H = 54
  const callout =
    attivo !== null
      ? {
          centre: Math.min(Math.max(x(attivo), PAD.left + CALLOUT_W / 2), W - PAD.right - CALLOUT_W / 2),
          dotX: x(attivo),
          dotY: y(points[attivo].diff),
        }
      : null

  return (
    <figure className="m-0">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full touch-manipulation"
        role="img"
        aria-label={`Andamento dello scarto nel set ${flow.set}, ${home} contro ${away}`}
        onMouseLeave={() => setAttivo(null)}
      >
        <defs>
          <clipPath id={`${idPrefix}-pos`}>
            <rect x="0" y="0" width={W} height={zeroY} />
          </clipPath>
          <clipPath id={`${idPrefix}-neg`}>
            <rect x="0" y={zeroY} width={W} height={H - zeroY} />
          </clipPath>
        </defs>

        {/* Le due metà: sopra lo zero è avanti la squadra di casa, sotto gli ospiti. */}
        <path d={area} fill={homeColor} fillOpacity="0.1" clipPath={`url(#${idPrefix}-pos)`} />
        <path d={area} fill={awayColor} fillOpacity="0.1" clipPath={`url(#${idPrefix}-neg)`} />
        <line x1={PAD.left} y1={zeroY} x2={W - PAD.right} y2={zeroY} stroke="var(--league-line-strong)" strokeWidth="1" />
        <path d={line} fill="none" stroke="var(--league-ink)" strokeWidth="1.25" opacity="0.45" />

        {/* Etichette dell'asse: solo il massimo, lo zero e il minimo. */}
        <text x={PAD.left - 8} y={y(maxAbs) + 4} textAnchor="end" fontSize="11" fill="var(--league-muted)">
          +{maxAbs}
        </text>
        <text x={PAD.left - 8} y={zeroY + 4} textAnchor="end" fontSize="11" fill="var(--league-muted)">
          0
        </text>
        <text x={PAD.left - 8} y={y(-maxAbs) + 4} textAnchor="end" fontSize="11" fill="var(--league-muted)">
          −{maxAbs}
        </text>

        {/* Tacche dell'asse degli scambi, dentro il grafico: 0, metà, fine. */}
        {[0, Math.round(maxX / 2), maxX].map((scambi) => (
          <text
            key={`x-${scambi}`}
            x={xAt(scambi)}
            y={PAD.top + PLOT_H - 4}
            textAnchor="middle"
            fontSize="9"
            fill="var(--league-muted)"
            opacity="0.65"
          >
            {scambi}
          </text>
        ))}

        {/* Il risultato del set, in coda alla curva. */}
        <text x={W - PAD.right + 10} y={y(final.diff) + 4} fontSize="13" fontWeight="600" fill="var(--league-ink)">
          {final.home}-{final.away}
        </text>
        <text x={W - PAD.right + 10} y={y(final.diff) + 18} fontSize="10" fill="var(--league-muted)">
          set {flow.set}
        </text>

        {/* Il cambio di campo del tie-break. */}
        {courtSwitch >= 0 && (
          <g pointerEvents="none">
            <line
              x1={x(courtSwitch)}
              y1={PAD.top}
              x2={x(courtSwitch)}
              y2={H - PAD.bottom}
              stroke="var(--league-line-strong)"
              strokeWidth="1.5"
            />
            <text x={x(courtSwitch)} y={PAD.top + 10} textAnchor="middle" fontSize="9" fill="var(--league-muted)">
              cambio campo
            </text>
          </g>
        )}

        {/* I segni: sopra la linea i time-out (col punteggio a cui sono stati
            chiesti), sotto la linea le sostituzioni (coi nomi). La x è la somma
            dei due punteggi — gli scambi giocati — quindi la posizione è esatta
            anche se time-out e sostituzioni non cadono su un cambio di servizio. */}
        {markers.map((marker, i) => {
          const cx = marker.total != null ? xAt(marker.total) : x(marker.index ?? 0)
          const colore = marker.side === 'home' ? homeColor : awayColor
          const sopra = marker.labelSide !== 'bottom'
          // Etichette **verticali**: sull'asse degli scambi i segni si addensano
          // (due punti = 14 px) e un'etichetta orizzontale coprirebbe quelle
          // vicine. In verticale ognuna resta sulla propria riga. Sono corte —
          // il numero o il punteggio — perché lo spazio sopra e sotto il grafico
          // è poco: i nomi stanno nel tooltip e nella lista sotto.
          const yEtichetta = sopra ? PAD.top - 6 : H - PAD.bottom + 6
          return (
            <g key={`marker-${i}`}>
              <line
                x1={cx}
                y1={PAD.top}
                x2={cx}
                y2={H - PAD.bottom}
                stroke={colore}
                strokeWidth="1"
                strokeDasharray="3 3"
                opacity="0.75"
              />
              <text
                x={cx}
                y={yEtichetta}
                textAnchor={sopra ? 'start' : 'end'}
                fontSize="9"
                fill={colore}
                transform={`rotate(-90 ${cx} ${yEtichetta})`}
              >
                {marker.label}
                <title>{marker.title ?? marker.label}</title>
              </text>
            </g>
          )
        })}

        {/* I cambi di servizio: un pallino per volta, colore = chi va a battere.
            Ogni pallino ha un'area di puntamento trasparente più grande, così è
            cliccabile anche su schermo piccolo. */}
        {points.map((p, i) => (
          <g key={i}>
            <circle
              cx={x(i)}
              cy={y(p.diff)}
              r={2.6}
              fill={p.side === 'home' ? homeColor : awayColor}
              stroke={attivo === i ? 'var(--league-ink)' : 'none'}
              strokeWidth="1.2"
            >
              <title>
                {`Cambio ${i + 1}: ${p.home}-${p.away} · va a battere ${(p.side === 'home' ? home : away).trim()} — ${serverLabel(p.player, p.number)}`}
              </title>
            </circle>
            <circle
              cx={x(i)}
              cy={y(p.diff)}
              r={9}
              fill="transparent"
              className="cursor-pointer"
              tabIndex={0}
              role="button"
              aria-label={`Cambio ${i + 1}: ${p.home}-${p.away}, va a battere ${(p.side === 'home' ? home : away).trim()}`}
              onMouseEnter={() => setAttivo(i)}
              onFocus={() => setAttivo(i)}
              onClick={() => setAttivo((corrente) => (corrente === i ? null : i))}
            />
          </g>
        ))}

        {/* Il callout: chi era al servizio in quel cambio. */}
        {callout && attivo !== null && (
          <g pointerEvents="none">
            <line
              x1={callout.dotX}
              y1={callout.dotY}
              x2={callout.centre}
              y2={PAD.top + CALLOUT_H}
              stroke="var(--league-line-strong)"
              strokeWidth="1"
            />
            <rect
              x={callout.centre - CALLOUT_W / 2}
              y={PAD.top}
              width={CALLOUT_W}
              height={CALLOUT_H}
              rx="6"
              fill="var(--league-surface)"
              stroke="var(--league-line-strong)"
            />
            <text x={callout.centre - CALLOUT_W / 2 + 10} y={PAD.top + 16} fontSize="11" fontWeight="600" fill="var(--league-ink)">
              Cambio {attivo + 1} · {points[attivo].home}-{points[attivo].away}
            </text>
            <text x={callout.centre - CALLOUT_W / 2 + 10} y={PAD.top + 31} fontSize="11" fill="var(--league-muted)">
              va a battere {(points[attivo].side === 'home' ? home : away).trim()}
            </text>
            <text x={callout.centre - CALLOUT_W / 2 + 10} y={PAD.top + 46} fontSize="12" fontWeight="600" fill="var(--league-ink)">
              {serverLabel(points[attivo].player, points[attivo].number)}
            </text>
          </g>
        )}
      </svg>

      <FlowCaption flow={flow} home={home} away={away} markers={markers} homeColor={homeColor} awayColor={awayColor} />
    </figure>
  )
}

/** Larghezza del grafico verticale, in unità di viewBox: è la larghezza di una
 *  colonna su un telefono, dove il disegno sta in scala ~1:1. */
const V_W = 340
/** Unità di viewBox per scambio giocato: passo **costante**, così la distanza
 *  fra due barre non dipende da quanto è durato il set. */
const V_STEP = 6
const V_PLOT_MIN = 240
const V_PLOT_MAX = 400
/** Altezza di una barra, in unità di viewBox: col passo di 6 resta un filo
 *  d'aria fra una barra e la successiva (una barra = un cambio di servizio). */
const BAR_H = 4.4
/** Lunghezza della barra quando lo scarto è **zero**: un mozzicone a cavallo
 *  dell'asse, perché una barra di lunghezza zero non si vedrebbe. */
const BAR_STUB = 3
const V_PAD = { top: 34, right: 80, bottom: 30, left: 80 }
// I due margini sono **uguali**, e non è un vezzo: l'asse degli scambi deve
// cadere nel **centro orizzontale** del disegno (vedi `zeroX`), quindi a sinistra
// e a destra del grafico ci vuole lo stesso spazio. Il valore viene dalla scritta
// **più lunga** che ci deve stare: l'etichetta di un time-out misura fino a ~70
// unità a 9 px («time-out 23-24») e va ancorata a 6 unità dal grafico, quindi
// serve un margine di almeno 76. Con 74 il controllo di
// `scripts/set-flow-chart-measure.mjs` trovava due etichette fuori dal viewBox di
// 1,3 e 2,3 unità, cioè tagliate dal bordo dell'SVG — e a occhio, in mezzo ai
// numeri, non si vede. Il margine destro, con «28 → 15», ne avanza.

/**
 * L'andamento dello stesso set, **disteso in verticale e a barre**, per gli
 * schermi stretti.
 *
 * Perché un disegno diverso e non lo stesso ruotato: su un telefono 50 scambi in
 * 340 px stanno a 6 px l'uno dall'altro e le etichette dei segni si accavallano.
 * Girando gli assi — gli **scambi scendono** verso il basso, lo **scarto** va a
 * sinistra (avanti la squadra di casa) e a destra (avanti gli ospiti) — la
 * larghezza smette di essere il vincolo: il grafico cresce in **altezza**, che su
 * uno schermo stretto è la dimensione che non manca mai. Il testo resta
 * **orizzontale**, quindi leggibile: è per questo che serve un disegno nuovo e
 * non una rotazione dell'SVG, che girerebbe anche le scritte.
 *
 * Perché **barre** e non una linea: due cambi di servizio distano 6 px, e la
 * linea che li unisce disegna una diagonale che non corrisponde a niente — i
 * cambi sono istanti, non un continuo. Una barra per cambio dice una cosa sola
 * per volta: **quanto** era lo scarto (la lunghezza) e **da che parte** (il
 * lato). Il pallino in punta resta chi **va a battere**: sono due informazioni
 * diverse e restano due segni diversi.
 *
 * La collocazione è la stessa dell'orizzontale (vedi `flowGeometry`): la
 * posizione è il numero di scambi giocati, quindi time-out e sostituzioni cadono
 * dove sono davvero, non sul cambio di servizio più vicino.
 */
export function SetFlowChartBars({
  flow,
  home,
  away,
  markers = [],
  homeColor = SERIE_HOME,
  awayColor = SERIE_AWAY,
}: Omit<SetFlowChartProps, 'idPrefix'>) {
  const [attivo, setAttivo] = useState<number | null>(null)
  const geo = flowGeometry(flow)
  if (!geo) return null
  const { points, maxAbs, totali, maxX, final, courtSwitch, timeline } = geo

  const plotH = Math.min(V_PLOT_MAX, Math.max(V_PLOT_MIN, maxX * V_STEP))
  const V_H = V_PAD.top + plotH + V_PAD.bottom
  const plotW = V_W - V_PAD.left - V_PAD.right
  const zeroX = V_PAD.left + plotW / 2
  const fondo = V_PAD.top + plotH

  const yAt = (scambi: number) => V_PAD.top + Math.min(1, Math.max(0, scambi / maxX)) * plotH
  const y = (index: number) => yAt(totali[index] ?? 0)
  const xOf = (diff: number) => zeroX - (diff / maxAbs) * (plotW / 2)
  const x = (index: number) => xOf(points[index].diff)
  /** Metà larghezza del grafico: dove arriva la barra dello scarto massimo. */
  const mezza = plotW / 2

  return (
    <figure className="m-0">
      <svg
        viewBox={`0 0 ${V_W} ${V_H}`}
        className="h-auto w-full touch-manipulation"
        role="img"
        aria-label={`Andamento dello scarto nel set ${flow.set}, ${home} contro ${away}`}
        onMouseLeave={() => setAttivo(null)}
      >
        {/* L'asse dello scarto: lo zero, in mezzo al disegno. Da qui partono le
            barre, e il lato dice chi era avanti. */}
        <line x1={zeroX} y1={V_PAD.top} x2={zeroX} y2={fondo} stroke="var(--league-line-strong)" strokeWidth="1" />

        {/* **Tutti gli scambi**, uno per barra: fra due cambi di servizio chi batte
            prende tutti gli scambi tranne l'ultimo, quindi anche i punti che il
            referto non registra sono determinati e qui diventano barre piene. Il
            **pallino** in punta resta solo dove il referto registra il cambio: è
            l'unico punto che il referto dichiara. A scarto zero la barra non avrebbe
            lunghezza: resta un mozzicone grigio a cavallo dell'asse. */}
        {timeline.map((s) => {
          const pari = s.diff === 0
          const lunghezza = pari ? BAR_STUB : (Math.abs(s.diff) / maxAbs) * mezza
          const x0 = pari ? zeroX - BAR_STUB / 2 : s.diff > 0 ? zeroX - lunghezza : zeroX
          return (
            <rect
              key={`barra-${s.rally}`}
              data-barra=""
              x={x0.toFixed(1)}
              y={(yAt(s.rally) - BAR_H / 2).toFixed(1)}
              width={lunghezza.toFixed(1)}
              height={BAR_H}
              rx="0.8"
              fill={pari ? 'var(--league-muted)' : s.diff > 0 ? homeColor : awayColor}
              opacity={pari ? 0.45 : 0.55}
            />
          )
        })}

        {/* Scala degli scarti, in cima: agli estremi il massimo vantaggio
            dell'una e dell'altra. Lo **zero non si etichetta**: è la linea
            centrale, dove l'asse degli scambi — che qui è anche l'ascissa — sta
            in mezzo al disegno, e scriverci «0» accanto al «0» degli scambi
            faceva due zeri appiccicati. */}
        <text x={V_PAD.left} y={V_PAD.top - 10} textAnchor="middle" fontSize="11" fill="var(--league-muted)">
          +{maxAbs}
        </text>
        <text x={V_W - V_PAD.right} y={V_PAD.top - 10} textAnchor="middle" fontSize="11" fill="var(--league-muted)">
          −{maxAbs}
        </text>

        {/* Niente scale sull'asse: i numeri degli scambi (0, metà, massimo)
            appesantivano il disegno senza aggiungere nulla a una barra per
            scambio. L'asse resta la linea centrale da cui partono le barre. */}

        {/* Il risultato del set: in fondo, dove finisce il grafico. */}
        <text x={V_PAD.left} y={V_H - 10} fontSize="12" fontWeight="600" fill="var(--league-ink)">
          {final.home}-{final.away}
          <tspan fontSize="10" fontWeight="400" fill="var(--league-muted)">
            {' '}
            · set {flow.set}
          </tspan>
        </text>

        {/* Il cambio di campo del tie-break. */}
        {courtSwitch >= 0 && (
          <g pointerEvents="none">
            <line
              x1={V_PAD.left}
              y1={yAt(totali[courtSwitch])}
              x2={V_W - V_PAD.right}
              y2={yAt(totali[courtSwitch])}
              stroke="var(--league-line-strong)"
              strokeWidth="1.5"
            />
            <text x={V_PAD.left + 4} y={yAt(totali[courtSwitch]) - 4} fontSize="9" fill="var(--league-muted)">
              cambio campo
            </text>
          </g>
        )}

        {/* I segni, sui **margini**: i time-out a sinistra, le sostituzioni a
            destra, col testo orizzontale. Sono corti — il punteggio o i due
            numeri — e qui ognuno ha la sua riga: l'accavallamento che c'era in
            orizzontale non c'è più. */}
        {markers.map((marker, i) => {
          const cy = yAt(marker.total ?? totali[marker.index ?? 0] ?? 0)
          const colore = marker.side === 'home' ? homeColor : awayColor
          const aSinistra = marker.labelSide !== 'bottom'
          return (
            <g key={`v-marker-${i}`}>
              <line
                x1={V_PAD.left}
                y1={cy}
                x2={V_W - V_PAD.right}
                y2={cy}
                stroke={colore}
                strokeWidth="1"
                strokeDasharray="3 3"
                opacity="0.75"
              />
              <text
                x={aSinistra ? V_PAD.left - 6 : V_W - V_PAD.right + 6}
                y={cy + 3}
                textAnchor={aSinistra ? 'end' : 'start'}
                fontSize="9"
                fill={colore}
              >
                {marker.label}
                <title>{marker.title ?? marker.label}</title>
              </text>
            </g>
          )
        })}

        {/* In punta alla barra, il **pallino** col colore di chi va a battere: la
            barra dice lo scarto, il pallino chi riprende il servizio. */}
        {points.map((p, i) => (
          <circle
            key={`pallino-${i}`}
            cx={x(i)}
            cy={y(i)}
            r={3}
            fill={p.side === 'home' ? homeColor : awayColor}
          >
            <title>
              {`Cambio ${i + 1}: ${p.home}-${p.away} · va a battere ${(p.side === 'home' ? home : away).trim()} — ${serverLabel(p.player, p.number)}`}
            </title>
          </circle>
        ))}

        {/* L'area di puntamento è la **barra intera**, non il pallino: su un
            telefono il pallino è 6 px, la barra è la cosa che si vede e si tocca.
            Non dà fastidio ai segni, che stanno nei margini. */}
        {points.map((p, i) => (
          <rect
            key={`tocco-${i}`}
            x={(Math.min(zeroX, x(i)) - 4).toFixed(1)}
            y={(y(i) - 5.5).toFixed(1)}
            width={Math.max(Math.abs(x(i) - zeroX) + 8, 12).toFixed(1)}
            height="11"
            fill="transparent"
            className="cursor-pointer"
            tabIndex={0}
            role="button"
            aria-label={`Cambio ${i + 1}: ${p.home}-${p.away}, va a battere ${(p.side === 'home' ? home : away).trim()}`}
            onMouseEnter={() => setAttivo(i)}
            onFocus={() => setAttivo(i)}
            onClick={() => setAttivo((corrente) => (corrente === i ? null : i))}
          />
        ))}
      </svg>

      {/* Il cambio selezionato: su schermo stretto un callout dentro il disegno
          coprirebbe mezza barra, quindi diventa una riga di testo sotto il
          grafico — dove il nome del battitore si legge per intero. */}
      {attivo !== null && (
        <p className="mt-2 rounded-lg border border-[color:var(--league-line-strong)] px-3 py-2 text-[12px] text-[color:var(--league-ink)]">
          <span className="font-semibold">Cambio {attivo + 1}</span> · {points[attivo].home}-{points[attivo].away} · va a
          battere {(points[attivo].side === 'home' ? home : away).trim()} —{' '}
          {serverLabel(points[attivo].player, points[attivo].number)}
        </p>
      )}

      <FlowCaption flow={flow} home={home} away={away} markers={markers} homeColor={homeColor} awayColor={awayColor} markerHint={false} />
    </figure>
  )
}
